'use client';

import { animated, easings, useReducedMotion, useSpring } from '@react-spring/web';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { useInertProps } from '../../hooks/use-inert-props';
import { useMeasure } from '../../hooks/use-measure';
import { useSlotContent } from '../../hooks/use-slot-content';
import { Slot } from '../slot';
import { AccordionItemContext } from './context/accordion-item.context';
import { accordionPanelStyles } from './styles/accordion-panel.styles';
import type { AccordionPanelPropsInterface } from './types/accordion-panel.types';

/**
 * Contains the content that is hidden and revealed by its sibling
 * `Accordion.Header`.
 *
 * @since 8.43.0
 */

export const AccordionPanel = forwardRef<HTMLDivElement, AccordionPanelPropsInterface>(
  (props: AccordionPanelPropsInterface, forwardedRef) => {
    const { asChild = false, children, className, css: cssProp, style, ...rest } = props;

    const reduceMotion = useReducedMotion();

    const { expanded, value } = useContext(AccordionItemContext);

    const [heightRef, { height }] = useMeasure();
    const animatedProps = useSpring({
      height: expanded ? height : 0,
      config: {
        easing: easings.easeInOutQuart,
        duration: reduceMotion ? 0 : 250,
      },
    });

    const styles = css(accordionPanelStyles, cssProp);

    const Component = animated(asChild ? Slot : 'div');

    const content = useSlotContent({
      children,
      asChild,
      template: (childContent) => (
        <div ref={heightRef} className={cx('c11n', styles, className)} style={{ ...style }}>
          {childContent}
        </div>
      ),
      dependencies: [heightRef, styles, className, style],
    });

    const inertProps = useInertProps(!expanded);

    return (
      <Component
        ref={forwardedRef as any}
        style={{ ...(animatedProps as any) }}
        role="region"
        id={`${value}-panel`}
        aria-labelledby={`${value}-header`}
        data-c11n-component="Accordion.Panel"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', css({ position: 'relative', overflow: 'hidden' }))}
        {...inertProps}
        {...rest}
      >
        {content}
      </Component>
    );
  },
);

AccordionPanel.displayName = 'Accordion.Panel';
